import React, { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getPublicPdfReport } from "../../redux/thunks/PublicResult";
import {
  ReportJobFitColors,
  ReportLabels,
  ReportLevelColors,
} from "../../utils/Constants";
import {
  formatReportDate,
  formatReportScore,
  getCandidateName,
  getNameInitials,
  getReportSections,
} from "../../utils/utilities";
import { ReactComponent as PoweredByIcon } from "../../assets/icons/Views/dashboard/test-detail/powered-by-testfuse.svg";
import { ReactComponent as CheckMarkIcon } from "../../assets/icons/Views/dashboard/test-detail/check-mark.svg";

const JobFitCard = ({ jobFit }) => {
  const colors = ReportJobFitColors[jobFit?.label];
  return (
    <div
      className="public-pdf-report-job-fit d-flex flex-column flex-shrink-0 align-items-center align-self-center align-self-md-start"
      style={{ gap: "4px" }}
    >
      {colors && (
        <span
          className="caption dark-100 public-pdf-report-job-fit-badge"
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
          }}
        >
          {jobFit.label}
        </span>
      )}
      <span className="body-2 dark-100">{ReportLabels.jobFitScore}</span>
      {jobFit ? (
        <span className="headline-2 primary-dark-text">
          {formatReportScore(jobFit.score)}
          <span className="body-1 dark-50">/10</span>
        </span>
      ) : (
        <span className="body-1 dark-50">{ReportLabels.jobFitUnavailable}</span>
      )}
      <span className="caption dark-50 text-center public-pdf-report-job-fit-note">
        {jobFit ? ReportLabels.jobFitNote : ReportLabels.jobFitUnavailableNote}
      </span>
    </div>
  );
};

const ScoreBar = ({ title, item }) => {
  const color = ReportLevelColors[item.label];
  return (
    <div
      className="public-pdf-report-score-item d-flex flex-column"
      style={{ gap: "4px" }}
    >
      <div className="d-flex" style={{ gap: "8px" }}>
        <span className="body-2 dark-100">{title}</span>
        <span className="body-2" style={{ color }}>
          {item.label}
        </span>
      </div>
      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        <div className="public-pdf-report-score-bar flex-grow-1">
          <div style={{ width: `${item.score * 10}%`, backgroundColor: color }} />
        </div>
        <span className="body-1 dark-50">
          <span style={{ color }}>{formatReportScore(item.score)}</span>/10
        </span>
      </div>
      <span className="body-3 dark-50">{item.description}</span>
    </div>
  );
};

const PointList = ({ items, icon }) => (
  <div className="d-flex flex-column" style={{ gap: "12px" }}>
    {items.map((point, index) => (
      <div className="d-flex flex-column" style={{ gap: "4px" }} key={index}>
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          {icon}
          <span className="body-2 dark-100">{point.title}</span>
        </div>
        {point.description && (
          <span className="body-3 dark-50" style={{ paddingLeft: "18px" }}>
            {point.description}
          </span>
        )}
      </div>
    ))}
  </div>
);

// public online page of the pdf report, the same design as the pdf
export default function PublicPdfAssessmentReport() {
  const params = useParams();
  const dispatch = useDispatch();
  const isOnMobileScreen = useMediaQuery("(max-width: 767px)");

  const publicResult = useSelector((state) => state.publicResultReducer);
  const publicReport = publicResult?.public_pdf_report;
  const errorMessage = publicResult?.error_message;

  useEffect(() => {
    dispatch(getPublicPdfReport({ id: params?.id }));
  }, [params?.id]);

  const candidateName = getCandidateName(publicReport?.candidate_name);
  const report = publicReport?.downloadable_report || {};
  const {
    scoreOverviewBars,
    keyStrengths,
    skillsInsights,
    areasToValidate,
    recommendation,
    statusColors,
    isCompactTable,
  } = getReportSections(report);
  const tableHeaderClass = isCompactTable ? "body-3" : "body-2";
  const tableCellClass = isCompactTable ? "caption" : "body-3";

  return (
    <div className="public-result-main-container">
      {publicReport ? (
        <div className="public-pdf-report-page d-flex flex-column text-start">
          <div
            className="public-pdf-report-header d-flex align-items-start justify-content-between"
            style={{ gap: "16px" }}
          >
            <div
              className="d-flex align-items-center"
              style={{ gap: "12px", minWidth: 0 }}
            >
              <div className="public-pdf-report-avatar d-flex align-items-center justify-content-center flex-shrink-0">
                <span className="headline-5 primary-text">
                  {getNameInitials(candidateName)}
                </span>
              </div>
              <div
                className="d-flex flex-column"
                style={{ gap: "4px", minWidth: 0 }}
              >
                <span
                  className={`${isOnMobileScreen ? "headline-5" : "headline-3"} dark-100`}
                >
                  {candidateName}
                </span>
                <span className="body-2 primary-dark-text">
                  {publicReport.role_title}
                </span>
              </div>
            </div>
            {(publicReport.company_logo_url || publicReport.company_name) && (
              <div
                className="public-pdf-report-company d-flex flex-shrink-0 align-items-center"
                style={{ gap: "8px" }}
              >
                {publicReport.company_logo_url && (
                  <img
                    className="public-pdf-report-company-logo"
                    src={publicReport.company_logo_url}
                    alt="Company logo"
                  />
                )}
                {publicReport.company_name && (
                  <span
                    className={`${publicReport.company_logo_url ? "body-2 public-pdf-report-company-name-with-logo" : "subtitle-2"} dark-100 public-pdf-report-company-name`}
                  >
                    {publicReport.company_name}
                  </span>
                )}
              </div>
            )}
          </div>

          <div
            className="public-pdf-report-content d-flex flex-column flex-grow-1"
            style={{ gap: "40px" }}
          >
            <div
              className="d-flex flex-column flex-md-row"
              style={{ gap: "24px" }}
            >
              <div
                className="public-pdf-report-summary d-flex flex-column"
                style={{ gap: "12px" }}
              >
                <span className="subtitle-2 dark-100">
                  {ReportLabels.executiveSummary}
                </span>
                <span className="body-2 dark-50">
                  {report.executive_summary}
                </span>
              </div>
              <div className="public-pdf-report-divider" />
              <JobFitCard jobFit={report.job_fit} />
            </div>

            {scoreOverviewBars.length > 0 && (
              <div className="d-flex flex-column" style={{ gap: "12px" }}>
                <span className="subtitle-2 dark-100">
                  {ReportLabels.scoreOverview}
                </span>
                <div className="public-pdf-report-score-overview">
                  {scoreOverviewBars.map(({ key, title }) => (
                    <ScoreBar
                      key={key}
                      title={title}
                      item={report.score_overview[key]}
                    />
                  ))}
                </div>
              </div>
            )}

            {(keyStrengths.length > 0 || skillsInsights.length > 0) && (
              // strengths and skills are vertically centered on each other, as in the design
              <div
                className="d-flex flex-column flex-md-row align-items-md-center"
                style={{ gap: "20px" }}
              >
                {keyStrengths.length > 0 && (
                  <div
                    className="public-pdf-report-strengths d-flex flex-column"
                    style={{ gap: "12px" }}
                  >
                    <span className="subtitle-2 dark-100">
                      {ReportLabels.keyStrengths}
                    </span>
                    <PointList
                      items={keyStrengths}
                      icon={<CheckMarkIcon className="flex-shrink-0" />}
                    />
                  </div>
                )}
                {keyStrengths.length > 0 && skillsInsights.length > 0 && (
                  <div className="public-pdf-report-divider" />
                )}
                {skillsInsights.length > 0 && (
                  <div
                    className="public-pdf-report-skills d-flex flex-column"
                    style={{ gap: "12px" }}
                  >
                    <span className="subtitle-2 dark-100">
                      {ReportLabels.skillsInsights}
                    </span>
                    <table
                      className={`public-pdf-report-skills-table ${
                        isCompactTable ? "public-pdf-report-skills-table-compact" : ""
                      }`}
                    >
                      <thead>
                        <tr>
                          {ReportLabels.skillsTableHeadings.map((heading) => (
                            <th
                              className={`${tableHeaderClass} dark-50`}
                              key={heading}
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {skillsInsights.map((row, index) => (
                          <tr key={index}>
                            <td className={`${tableCellClass} dark-100`}>
                              {row.skill}
                            </td>
                            <td className={`${tableCellClass} dark-100`}>
                              {row.level_assessed}
                            </td>
                            <td
                              className={tableCellClass}
                              style={{ color: ReportLevelColors[row.match] }}
                            >
                              {row.match}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div
              className="d-flex flex-column flex-md-row align-items-md-start"
              style={{ gap: "20px" }}
            >
              {areasToValidate.length > 0 && (
                <div
                  className="public-pdf-report-areas d-flex flex-column"
                  style={{ gap: "12px" }}
                >
                  <span className="subtitle-2 dark-100">
                    {ReportLabels.areasToValidate}
                  </span>
                  <PointList
                    items={areasToValidate}
                    icon={
                      <span
                        className="body-2"
                        style={{ color: ReportLevelColors.Good }}
                      >
                        →
                      </span>
                    }
                  />
                </div>
              )}
              {areasToValidate.length > 0 && (
                <div className="public-pdf-report-divider" />
              )}
              <div
                className="public-pdf-report-recommendation d-flex flex-column"
                style={{ gap: "12px" }}
              >
                <span className="subtitle-2 dark-100">
                  {ReportLabels.hiringRecommendation}
                </span>
                {statusColors && (
                  <span
                    className="body-2 public-pdf-report-status align-self-start"
                    style={{
                      backgroundColor: statusColors.background,
                      color: statusColors.text,
                    }}
                  >
                    {recommendation.status}
                  </span>
                )}
                <span className="body-3 dark-50">
                  {recommendation.description}
                </span>
              </div>
            </div>
          </div>

          <div
            className="public-pdf-report-footer d-flex align-items-center justify-content-between"
            style={{ gap: "16px" }}
          >
            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <span className="caption dark-50">
                {ReportLabels.assessmentDate}
              </span>
              <span className="caption dark-100">
                {formatReportDate(publicReport.assessment_date)}
              </span>
            </div>
            <PoweredByIcon />
          </div>
        </div>
      ) : errorMessage ? (
        <div className="default-public-result-card-container">
          <div className="default-public-result-alert">
            <span className="body-1 dark-100 text-center">
              {errorMessage === "No Record Found"
                ? "Report not found."
                : errorMessage}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="w-100 d-flex align-items-center justify-content-center"
          style={{ height: "100vh" }}
        >
          <div
            className="processing-indicator primary"
            style={{ width: "40px", height: "40px" }}
          />
        </div>
      )}
    </div>
  );
}
